import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase, ShieldCheck, ShieldAlert, Award, Trophy, Medal,
  CheckCircle2, ArrowUpRight, ArrowRight, Users, FileWarning,
  Clock4, BadgeCheck, Sparkles, TrendingUp, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const HIRING_RISKS = [
  { icon: FileWarning, label: "Fake experience" },
  { icon: ShieldAlert, label: "Undisclosed misconduct" },
  { icon: Clock4, label: "Attendance issues" },
  { icon: BadgeCheck, label: "Credential fraud" },
  { icon: Users, label: "Frequent job hopping" },
];

const ACCREDITATION_TIERS = [
  {
    level: "Level 1",
    icon: Medal,
    accent: "from-amber-400 to-amber-600",
    badgeBg: "bg-amber-50",
    badgeText: "text-amber-700",
    border: "border-amber-300",
    title: "EthicCred Certified Employer",
    glyph: "🥉",
    summary:
      "Awarded to employers that successfully complete EthicCred verification and maintain documented employment, compliance, and workforce records.",
    meaning: "Verified. Compliant. Recognized.",
  },
  {
    level: "Level 2",
    icon: Award,
    accent: "from-zinc-300 to-zinc-500",
    badgeBg: "bg-zinc-100",
    badgeText: "text-zinc-700",
    border: "border-zinc-400",
    title: "EthicCred Trusted Employer",
    glyph: "🥈",
    summary:
      "Awarded to organizations demonstrating transparency, ethical workplace practices, employee accountability, and responsible workforce management.",
    meaning: "Trusted by Employees. Trusted by Industry.",
  },
  {
    level: "Level 3",
    icon: Trophy,
    accent: "from-yellow-300 to-yellow-500",
    badgeBg: "bg-yellow-50",
    badgeText: "text-yellow-800",
    border: "border-yellow-400",
    title: "EthicCred Employer of Excellence",
    glyph: "🥇",
    summary:
      "The highest employer recognition awarded to organizations that consistently demonstrate outstanding hiring practices, employee development, workplace culture, and organizational integrity.",
    meaning: "A Benchmark for Workplace Excellence.",
    highlight: true,
  },
];

const WHY_BULLETS = [
  "Reduce hiring risks",
  "Verify candidate claims before onboarding",
  "Identify credential discrepancies",
  "Improve workforce reliability",
  "Strengthen workplace culture",
  "Protect organizational reputation",
  "Support compliant hiring practices",
  "Make faster, evidence-based decisions",
];

export default function Employers() {
  return (
    <div className="bg-ec-bg text-ec-ink min-h-screen" data-testid="employers-page">
      <Navbar />
      <Hero />
      <RisksBanner />
      <AccreditationSection />
      <HierarchySection />
      <WhySection />
      <BadgesShowcase />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16 lg:pt-28 lg:pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow" data-testid="employers-eyebrow">
            <Briefcase className="w-3.5 h-3.5 text-ec-emerald" />
            For Employers
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6">
            Hire with{" "}
            <span className="relative inline-block">
              Confidence.
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" preserveAspectRatio="none">
                <path d="M2 8 Q 75 2 150 6 T 298 4" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-ec-ink-soft mt-7 max-w-2xl leading-relaxed">
            Every hire carries risk. EthicCred helps employers make{" "}
            <span className="font-semibold text-ec-ink">informed hiring decisions</span> through
            verified employment records, professional credentials, workplace conduct history, and
            trusted digital accreditations.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3" data-testid="employers-cta-row">
            <Link to="/register?role=organization" data-testid="employers-cta-register">
              <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-12 text-base gap-2">
                Register your Company <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/demo?audience=employer" data-testid="employers-cta-demo">
              <Button variant="outline" className="rounded-sm border-ec-ink/20 px-6 h-12 text-base gap-2">
                Request a Demo
              </Button>
            </Link>
            <Link to="/login" data-testid="employers-cta-signin">
              <Button variant="ghost" className="rounded-sm px-4 h-12 text-base text-ec-ink-soft hover:text-ec-ink">
                Sign in <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>
          <p className="text-xs text-ec-ink-soft mt-4 font-mono-plex" data-testid="employers-recognition-link">
            Already verifying with EthicCred?{" "}
            <Link to="/get-recognized" className="underline text-ec-ink hover:text-ec-emerald">
              Apply for employer accreditation →
            </Link>
          </p>
        </motion.div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="relative bg-white border border-ec-ink/10 p-8 noise-overlay shadow-[0_30px_80px_-20px_rgba(0,0,0,0.18)]" data-testid="employers-hero-card">
            <div className="absolute -top-3 -left-3 px-3 py-1.5 bg-ec-emerald text-ec-ink font-display font-black text-xs rotate-[-3deg] shadow-md">
              HIRE WITH EVIDENCE
            </div>
            <div className="space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-ec-line">
                <div className="w-10 h-10 bg-ec-ink text-white flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="eyebrow text-ec-emerald">Pre-onboarding</div>
                  <div className="font-display font-bold">Verified Employment Record</div>
                </div>
              </div>
              {[
                ["Identity", "EthicCred ID #EC-72KQ-91"],
                ["Employment", "5 verified · 0 unverified"],
                ["Conduct Score", "92 / 100"],
                ["Credential Match", "Aligned"],
                ["Risk Flags", "0"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between text-sm">
                  <span className="text-ec-ink-soft eyebrow">{k}</span>
                  <span className="font-mono-plex font-bold text-ec-ink">{v}</span>
                </div>
              ))}
              <div className="pt-4 mt-4 border-t border-ec-line flex items-center justify-between">
                <span className="font-display font-black text-xl">Verdict</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-ec-emerald/15 text-ec-emerald text-sm font-bold border border-ec-emerald/30">
                  <CheckCircle2 className="w-4 h-4" /> Cleared to hire
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function RisksBanner() {
  return (
    <section className="border-y border-ec-line bg-ec-ink text-white" data-testid="employers-risks">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14">
        <div className="text-center mb-8">
          <div className="eyebrow text-ec-emerald">The cost of one bad hire</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">
            Every hire carries risk.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {HIRING_RISKS.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.label}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="border border-white/10 bg-white/[0.04] p-4 hover:bg-white/[0.08] hover:border-ec-emerald/50 transition-colors"
                data-testid={`risk-${i + 1}`}
              >
                <Icon className="w-5 h-5 text-ec-emerald" />
                <div className="mt-3 font-display font-bold text-sm sm:text-base leading-tight">{r.label}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AccreditationSection() {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24" data-testid="employers-accreditation">
      <div className="text-center mb-14">
        <div className="eyebrow">EthicCred Employer Accreditations</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
          Three tiers. <span className="text-ec-emerald">One signal of trust.</span>
        </h2>
        <p className="text-ec-ink-soft mt-4 max-w-2xl mx-auto text-lg">
          Earn an accreditation that tells candidates, partners, and regulators exactly what your
          organization stands for.
        </p>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        {ACCREDITATION_TIERS.map((t, i) => {
          const Icon = t.icon;
          return (
            <motion.div
              key={t.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative bg-white border ${t.highlight ? "border-ec-ink shadow-[0_25px_60px_-20px_rgba(0,0,0,0.25)] lg:-translate-y-3" : "border-ec-line"} p-7 flex flex-col`}
              data-testid={`tier-${i + 1}`}
            >
              <div className={`absolute -top-3 right-5 inline-flex items-center gap-1.5 px-2.5 py-1 ${t.badgeBg} ${t.badgeText} border ${t.border} text-[10px] font-bold uppercase tracking-wider`}>
                <span>{t.glyph}</span> {t.level}
              </div>
              <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${t.accent} text-white shadow-md`}>
                <Icon className="w-7 h-7" />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-black mt-5 leading-tight">{t.title}</h3>
              <p className="text-ec-ink-soft mt-3 text-sm leading-relaxed flex-1">{t.summary}</p>
              <div className="mt-5 pt-5 border-t border-ec-line">
                <div className="eyebrow text-ec-ink-soft">Meaning</div>
                <p className="font-display font-bold text-base mt-1 text-ec-ink">{t.meaning}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function HierarchySection() {
  return (
    <section className="bg-ec-soft border-y border-ec-line" data-testid="employers-hierarchy">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
        <div className="text-center mb-12">
          <div className="eyebrow text-ec-emerald">Accreditation Hierarchy</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3">
            Climb the trust ladder.
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-2 items-stretch">
          {ACCREDITATION_TIERS.map((t, i) => (
            <div key={t.title} className="flex items-center gap-2">
              <div className="flex-1 bg-white border border-ec-line p-5 text-center relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-ec-ink text-white text-[10px] font-bold uppercase tracking-wider font-mono-plex">
                  {t.level}
                </div>
                <div className="text-3xl mt-2">{t.glyph}</div>
                <div className="font-display font-black text-sm mt-2 leading-tight">{t.title.replace("EthicCred ", "")}</div>
              </div>
              {i < ACCREDITATION_TIERS.length - 1 && (
                <ArrowRight className="hidden sm:block w-6 h-6 text-ec-ink/40 shrink-0" />
              )}
            </div>
          ))}
        </div>
        <p className="text-center font-display text-xl sm:text-2xl font-bold mt-12 max-w-3xl mx-auto">
          Build a workforce based on{" "}
          <span className="text-ec-emerald">trust, accountability, and transparency.</span>
        </p>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24" data-testid="employers-why">
      <div className="text-center mb-12">
        <div className="eyebrow">Why Employers Choose EthicCred</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
          Eight outcomes every <br className="hidden sm:block" />hiring team measures.
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {WHY_BULLETS.map((b, i) => (
          <motion.div
            key={b}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.04 }}
            className="bg-white border border-ec-line p-5 flex items-start gap-3 hover:border-ec-emerald/40 hover:shadow-md transition-all"
            data-testid={`why-bullet-${i + 1}`}
          >
            <CheckCircle2 className="w-5 h-5 text-ec-emerald mt-0.5 shrink-0" />
            <div className="font-semibold text-ec-ink">{b}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function BadgesShowcase() {
  return (
    <section className="bg-ec-ink text-white" data-testid="employers-badges">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-1 text-xs eyebrow text-white">
              <Sparkles className="w-3 h-3 text-ec-emerald" />
              Professional Accreditations & Trust Badges
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-5">
              Recognize and reward workplace <span className="text-ec-emerald">excellence</span> through verified digital credentials.
            </h2>
            <p className="text-zinc-300 mt-5 leading-relaxed">
              Issue tamper-proof badges, share verifiable widgets on your careers page, and let every
              candidate confirm your credentials in one scan.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/get-recognized" data-testid="badges-cta-recognized">
                <Button className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink px-6 h-12 text-base font-bold gap-2">
                  Apply for Accreditation <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/demo?audience=employer" data-testid="badges-cta-demo">
                <Button variant="outline" className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 px-6 h-12 text-base">
                  See it live
                </Button>
              </Link>
            </div>
          </div>
          <ul className="grid grid-cols-1 gap-3">
            {[
              { icon: BadgeCheck, label: "Issuer-signed digital badge" },
              { icon: ShieldCheck, label: "QR + public verification URL" },
              { icon: TrendingUp, label: "Live trust score on every record" },
              { icon: Building2, label: "Embed widget for careers / website" },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <li key={b.label} className="flex items-center gap-3 border border-white/10 bg-white/[0.04] p-4">
                  <Icon className="w-5 h-5 text-ec-emerald" />
                  <span className="font-display font-semibold">{b.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="max-w-5xl mx-auto px-6 sm:px-10 py-24 text-center" data-testid="employers-final-cta">
      <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
        Hire faster. Hire smarter. <br />
        <span className="text-ec-emerald">Hire with EthicCred.</span>
      </h2>
      <p className="text-ec-ink-soft mt-4 max-w-xl mx-auto text-lg">
        Onboard your hiring team in under 10 minutes. Pilot with a single role. Roll out across every
        department when you&apos;re ready.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/register?role=organization">
          <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-12 text-base gap-2" data-testid="final-cta-register">
            Register your Company <ArrowUpRight className="w-4 h-4" />
          </Button>
        </Link>
        <Link to="/demo?audience=employer">
          <Button variant="outline" className="rounded-sm border-ec-ink/20 px-6 h-12 text-base" data-testid="final-cta-demo">
            Request a Demo
          </Button>
        </Link>
        <Link to="/login">
          <Button variant="ghost" className="rounded-sm px-4 h-12 text-base text-ec-ink-soft" data-testid="final-cta-signin">
            Sign in
          </Button>
        </Link>
      </div>
    </section>
  );
}
